import React, { useState } from "react";
import { Stethoscope, Loader2, CheckCircle2, XCircle, AlertTriangle, PlugZap, KeyRound, Webhook, Copy, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Flag = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{label}
  </span>
);

const Block = ({ title, res, testid }) => {
  if (!res) return null;
  const d = res.data?.data && Array.isArray(res.data.data) ? res.data.data : res.data;
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3 text-xs space-y-1.5" data-testid={testid}>
      <div className="flex items-center justify-between"><b>{title}</b><Flag ok={!!res.ok} label={res.ok ? "OK" : `خطأ ${res.http || ""}`} /></div>
      {res.error && <div className="rounded-md bg-destructive/10 border border-destructive/30 p-2 text-destructive whitespace-pre-wrap" dir="auto" data-testid={`${testid}-error`}>{res.error.message}{res.error.user_msg ? ` — ${res.error.user_msg}` : ""}{res.error.code != null ? ` (code ${res.error.code}${res.error.subcode ? `/${res.error.subcode}` : ""})` : ""}</div>}
      {res.ok && <pre className="overflow-auto max-h-56 text-[11px] leading-5 text-muted-foreground" dir="ltr">{JSON.stringify(d, null, 1)}</pre>}
    </div>
  );
};

export const WhatsAppDiagnostics = () => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [pin, setPin] = useState("");
  const run = async () => {
    setBusy(true);
    try { setRes((await api.get("/wa/diagnose")).data); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const act = async (path, body) => {
    setBusy(true);
    try { await api.post(path, body || {}); toast.success("تم — أعد الفحص للتأكد"); await run(); } catch (e) { toast.error(formatError(e)); setBusy(false); }
  };
  const cfg = res?.configured || {};
  const [hook, setHook] = useState(null);
  const [nums, setNums] = useState(null);
  const [bizId, setBizId] = useState("");
  const loadNums = async () => {
    setBusy(true);
    try { const d = (await api.get("/wa/numbers")).data; setNums(d); setBizId(d.active?.business_id || ""); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const saveBiz = async () => {
    try { await api.post("/wa/business-id", { business_id: bizId }); toast.success("تم حفظ معرّف النشاط"); await loadNums(); } catch (e) { toast.error(formatError(e)); }
  };
  const activate = async (n) => {
    setBusy(true);
    try { await api.post("/wa/active-number", { phone_id: n.id, waba_id: n.waba_id }); toast.success(`الرقم النشط الآن: ${n.display_phone_number}`); await loadNums(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const showHook = async () => {
    try { setHook((await api.get("/wa/webhook-config")).data); } catch (e) { toast.error(formatError(e)); }
  };
  const copy = (v, label) => { navigator.clipboard.writeText(v); toast.success(`تم نسخ ${label}`); };
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="wa-diagnostics">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2"><Stethoscope className="w-4 h-4 text-primary" /><div><div className="font-bold text-sm">واتساب للأعمال — فحص الربط</div><div className="text-[11px] text-muted-foreground">حالة الرقم والتحقق والتسجيل واشتراك التطبيق كما تعيدها Meta فعليًا</div></div></div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={loadNums} disabled={busy} className="gap-1" data-testid="wa-numbers-btn"><Phone className="w-3.5 h-3.5" /> الأرقام</Button>
          <Button size="sm" variant="outline" onClick={showHook} className="gap-1" data-testid="wa-webhook-config-btn"><Webhook className="w-3.5 h-3.5" /> إعداد الـWebhook</Button>
          <Button size="sm" onClick={run} disabled={busy} className="gap-1" data-testid="wa-diagnose-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stethoscope className="w-3.5 h-3.5" />} فحص الآن</Button>
        </div>
      </div>
      {nums && (
        <div className="rounded-xl border border-border bg-background/60 p-3 text-xs space-y-2" data-testid="wa-numbers">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold">أرقام واتساب المتاحة للتوكن</span>
            <span className="text-muted-foreground">— معرّف النشاط (Business ID) لاكتشاف كل الحسابات:</span>
            <Input value={bizId} onChange={(e) => setBizId(e.target.value.replace(/\D/g, ""))} placeholder="مثال 9616…" className="h-7 w-44 text-xs" dir="ltr" data-testid="wa-business-id-input" />
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={saveBiz} disabled={!bizId} data-testid="wa-business-id-save">حفظ</Button>
          </div>
          {nums.numbers.length === 0 && <div className="text-muted-foreground">لا أرقام — أدخل معرّف النشاط أو تحقق من التوكن.</div>}
          {nums.numbers.map((n) => (
            <div key={n.id} className={`flex items-center justify-between gap-2 rounded-lg border p-2 ${n.active ? "border-emerald-500/50 bg-emerald-500/5" : "border-border"}`} data-testid={`wa-number-${n.id}`}>
              <div className="min-w-0">
                <div className="font-bold" dir="ltr">{n.display_phone_number} <span className="font-normal text-muted-foreground">· {n.verified_name}</span></div>
                <div className="text-[10px] text-muted-foreground" dir="ltr">WABA {n.waba_id} ({n.waba_name}) · Phone ID {n.id} · {n.status} / {n.platform_type} · {n.quality_rating}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Flag ok={n.ready} label={n.ready ? "جاهز" : n.status} />
                {n.active ? <span className="text-[11px] text-emerald-600 font-bold" data-testid={`wa-number-active-${n.id}`}>النشط</span>
                  : <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy || !n.ready} onClick={() => activate(n)} data-testid={`wa-number-activate-${n.id}`}>تفعيل كرقم رواج</Button>}
              </div>
            </div>
          ))}
          <div className="text-[10px] text-muted-foreground">الرقم النشط هو الذي يرسل منه حمد والفريق الرسائل الجديدة؛ الردود على العملاء تخرج دائمًا من الرقم الذي راسلوه.</div>
        </div>
      )}
      {hook && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-xs space-y-2" data-testid="wa-webhook-config">
          <div className="font-bold">الصقها في Meta → WhatsApp → Configuration → Webhooks</div>
          {[["Callback URL", hook.callback_url, "wa-callback-url"], ["Verify token", hook.verify_token, "wa-verify-token"]].map(([label, val, tid]) => (
            <div key={tid} className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
              <code className="flex-1 truncate rounded bg-background px-2 py-1 font-mono text-[11px]" dir="ltr" data-testid={tid}>{val || "— غير مُعدّ"}</code>
              <Button size="sm" variant="ghost" className="h-7 px-2" disabled={!val} onClick={() => copy(val, label)} data-testid={`${tid}-copy`}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          <div className="text-[11px] text-muted-foreground">ثم فعّل حقل <b>messages</b> في Webhook fields، وفي «Step 2 → Register your WhatsApp phone number» فعّل مفتاح Subscribe webhooks لحساب رقم الاختبار.</div>
        </div>
      )}
      {res && (
        <>
          <div className="flex flex-wrap gap-1.5" data-testid="wa-configured">
            <Flag ok={cfg.token} label="Token" /><Flag ok={cfg.phone_id} label="Phone ID" /><Flag ok={cfg.waba_id} label="WABA ID" /><Flag ok={cfg.verify_token} label="Verify token" /><Flag ok={cfg.app_secret} label="App secret" />
          </div>
          <ul className="space-y-1.5" data-testid="wa-hints">
            {(res.hints || []).map((h, i) => <li key={i} className="flex items-start gap-2 text-xs rounded-lg bg-amber-500/10 border border-amber-500/30 p-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" /><span>{h}</span></li>)}
          </ul>
          <div className="grid md:grid-cols-2 gap-3">
            <Block title="التوكن (me)" res={res.token} testid="wa-token" />
            {res.token_debug && <Block title="صلاحيات التوكن" res={{ ok: res.token_debug.ok && res.token_debug.valid, data: res.token_debug, error: res.token_debug.error }} testid="wa-token-debug" />}
            <Block title="الرقم" res={res.phone} testid="wa-phone" />
            <Block title="حساب واتساب للأعمال" res={res.waba} testid="wa-waba" />
            <Block title="أرقام الحساب" res={res.waba_phones} testid="wa-waba-phones" />
            <Block title="اشتراك التطبيق (Webhook)" res={res.subscribed_apps} testid="wa-subs" />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button size="sm" variant="outline" className="gap-1" disabled={busy || !cfg.waba_id} onClick={() => act("/wa/subscribe-app")} data-testid="wa-subscribe-btn"><PlugZap className="w-3.5 h-3.5" /> اشتراك التطبيق بالحساب</Button>
            <div className="flex items-center gap-1">
              <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="PIN 6 أرقام" className="h-8 w-28 text-xs" dir="ltr" data-testid="wa-pin-input" />
              <Button size="sm" variant="outline" className="gap-1" disabled={busy || pin.length !== 6 || !cfg.phone_id} onClick={() => act("/wa/register", { pin })} data-testid="wa-register-btn"><KeyRound className="w-3.5 h-3.5" /> تسجيل الرقم</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
